import { styled } from "@mui/material/styles";
import { Box, Paper } from "@mui/material";

export const EditorContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullWidth" && prop !== "error",
})<{ fullWidth?: boolean; error?: boolean }>(({ theme, fullWidth, error }) => ({
  width: fullWidth ? "100%" : "auto",
  "& .rich-text-editor": {
    backgroundColor: theme.palette.background.paper,
    borderColor: error ? theme.palette.error.main : theme.palette.divider,
  },
  "& .error-text": {
    color: theme.palette.error.main,
    fontSize: theme.typography.caption.fontSize,
    marginTop: theme.spacing(0.5),
  },
  "& .helper-text": {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    marginTop: theme.spacing(0.5),
  },
  "& .readonly": {
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    "& .mdxeditor, & .markdown-content": {
      color: theme.palette.text.primary,
    },
    "& .mdxeditor ul": {
      listStyleType: "disc",
      listStylePosition: "outside",
      paddingLeft: theme.spacing(3),
      margin: theme.spacing(1, 0),
    },
    "& .mdxeditor ol": {
      listStyleType: "decimal",
      listStylePosition: "outside",
      paddingLeft: theme.spacing(3),
      margin: theme.spacing(1, 0),
    },
    "& .mdxeditor li": {
      margin: theme.spacing(0.5, 0),
    },
  },
  "& .editable": {
    borderRadius: theme.shape.borderRadius + "px",
    color: theme.palette.text.primary,
    backgroundColor: "#fcf8f4",
    "& .mdxeditor-toolbar": {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.5),
      "& button": {
        padding: theme.spacing(0.5),
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text.secondary,
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&.active": {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.action.selected,
        },
      },
      "& select": {
        padding: theme.spacing(0.5),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: theme.typography.body2.fontSize,
        "&:focus": {
          borderColor: theme.palette.primary.main,
          outline: "none",
        },
      },
    },
    "&:hover": {
      borderColor: theme.palette.text.primary,
    },
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      boxShadow: "none",
    },
    "& .mdxeditor": {
      minHeight: "100px",
      padding: 0,
    },
  },
}));

export const ReadOnlyContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "maxLines",
})<{ maxLines?: number }>(({ theme, maxLines }) => ({
  maxHeight: maxLines ? `${maxLines * 20}px` : "auto",
  overflow: "auto",
  color: theme.palette.text.primary,
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  "& .mdxeditor, & .markdown-content": {
    color: theme.palette.text.primary,
  },
  '& .mdxeditor [contenteditable="false"]': {
    padding: 0,
  },
  "& .mdxeditor ul": {
    listStyleType: "disc",
    listStylePosition: "outside",
    paddingLeft: theme.spacing(3),
    margin: theme.spacing(1, 0),
  },
  "& .mdxeditor ol": {
    listStyleType: "decimal",
    listStylePosition: "outside",
    paddingLeft: theme.spacing(3),
    margin: theme.spacing(1, 0),
  },
  "& .mdxeditor li": {
    margin: theme.spacing(0.5, 0),
  },
  "& .mdxeditor p": {
    margin: 0,
  },
}));

export const EditorPaper = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== "readOnly" && prop !== "maxLines" && prop !== "fullWidth" && prop !== "error",
})<{ readOnly?: boolean; maxLines?: number; fullWidth?: boolean; error?: boolean }>(
  ({ theme, maxLines, fullWidth, error }) => ({
    width: fullWidth ? "100%" : "auto",
    maxHeight: maxLines ? `${maxLines * 25}px` : "auto",
    borderColor: error ? theme.palette.error.main : theme.palette.divider,
    overflow: "auto",
    padding: 0,
    ...theme.typography.body1,
    border: `1px solid ${
      theme.palette.mode === "light"
        ? "rgba(0, 0, 0, 0.23)"
        : "rgba(255, 255, 255, 0.23)"
    }`,
    borderRadius: theme.shape.borderRadius,
    position: "relative",
    "&:hover": {
      borderColor: theme.palette.text.primary,
    },
    "&:focus-within": {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
      boxShadow: error
        ? `0 0 0 2px ${theme.palette.error.main}40`
        : `0 0 0 2px ${theme.palette.primary.main}40`,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      borderColor: theme.palette.action.disabled,
      cursor: "default",
    },
  })
);